import {
  checkResponseStatus,
  formatApiError,
  reportApiError,
} from '../utils/errorHandler'

const serverMap: Record<'gk-admin.duyangsong.cn' | 'test', string> = {
  'gk-admin.duyangsong.cn':
    'https://api.duyangsong.cn',
  test: 'https://gk-regir-docker-nvlgswjdem.cn-shenzhen.fcapp.run',
}
const currentHost = window.location.hostname
export let serverUrl = serverMap['test']
if (currentHost == 'gk-admin.duyangsong.cn') {
  serverUrl = serverMap['gk-admin.duyangsong.cn']
}

// API 基础配置
// 开发环境使用 /api 代理，生产环境使用完整 URL
export const API_BASE_URL = import.meta.env.DEV
  ? '/api'
  : serverUrl || 'http://localhost:8080'

// 通用响应类型
export interface ApiResponse<T = any> {
  code: string
  msg: string
  data: T
}

// 分页响应数据
export interface PaginatedResponse<T> {
  records: Array<T>
  total: number
  size: number
  current: number
  pages: number
}

// HTTP 方法枚举
export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

// API 请求配置
export interface ApiRequestConfig {
  method: HttpMethod
  headers?: Record<string, string>
  body?: any
}

// Token刷新状态管理
let isRefreshing = false
let refreshPromise: Promise<any> | null = null

// Token过期处理函数 - 先尝试刷新，失败后再跳转登录
async function handleTokenExpired() {
  // 如果正在刷新，等待刷新完成
  if (isRefreshing && refreshPromise) {
    return refreshPromise
  }

  // 检查是否有refresh token
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    // 没有refresh token，直接跳转登录
    handleGlobalTokenExpired()
    return Promise.reject(new Error('No refresh token available'))
  }

  isRefreshing = true
  refreshPromise = attemptTokenRefresh()

  try {
    await refreshPromise
    return Promise.resolve()
  } catch (error) {
    // 刷新失败，跳转登录
    handleGlobalTokenExpired()
    return Promise.reject(error)
  } finally {
    isRefreshing = false
    refreshPromise = null
  }
}

// 尝试刷新token
async function attemptTokenRefresh() {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    throw new Error('No refresh token available')
  }

  const url = `${API_BASE_URL}/user/refresh?refreshToken=${encodeURIComponent(refreshToken)}`

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  })

  const result = await response.json()

  if (!checkResponseStatus(result.code, result.msg)) {
    throw new Error(result.msg || 'Token refresh failed')
  }

  // 保存新的access token
  if (result.data) {
    localStorage.setItem('access_token', result.data)
  } else {
    throw new Error('Invalid refresh response')
  }

  return result
}

// 全局token过期处理函数
function handleGlobalTokenExpired() {
  // 清除所有tokens
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('token') // 清理旧token

  // 检查当前页面是否已经是登录页，避免不必要的跳转
  if (window.location.pathname !== '/login') {
    window.location.href = '/login'
  }
}

// 通用 API 请求函数
export async function apiRequest<T>(
  endpoint: string,
  config: ApiRequestConfig = { method: HttpMethod.GET },
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`

  const headers: Record<string, string> = {
    ...config.headers,
  }

  // 只有在非 FormData 时才设置 Content-Type
  if (!(config.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json'
  }

  // 如果有 access_token，添加到 Authorization header
  const accessToken = localStorage.getItem('access_token')
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`
  }

  const requestOptions: RequestInit = {
    method: config.method,
    headers,
  }

  if (config.body && config.method !== HttpMethod.GET) {
    if (config.body instanceof FormData) {
      requestOptions.body = config.body
    } else {
      requestOptions.body = JSON.stringify(config.body)
    }
  }

  try {
    const response = await fetch(url, requestOptions)
    const result = await response.json()

    // 检查是否为token过期或认证失败
    if (
      result.code === '40100' ||
      result.code === '40101' ||
      result.code === '40102'
    ) {
      // 尝试刷新token
      try {
        await handleTokenExpired()

        // 刷新成功，重新发起请求
        const newAccessToken = localStorage.getItem('access_token')
        if (newAccessToken) {
          headers.Authorization = `Bearer ${newAccessToken}`
          const retryResponse = await fetch(url, { ...requestOptions, headers })
          return await retryResponse.json()
        }
      } catch (refreshError) {
        // 刷新失败，抛出原始错误
        throw new Error('Token已过期或认证失败')
      }
    }

    // 检查业务状态码
    if (!checkResponseStatus(result.code, result.msg)) {
      // 上报API请求错误到Sentry（业务逻辑错误）
      reportApiError(new Error(result.msg || '业务逻辑错误'), {
        url: endpoint,
        method: config.method,
        code: result.code,
        message: result.msg,
      })

      throw new Error(result.msg || `HTTP error! status: ${response.status}`)
    }

    return result
  } catch (error) {
    const errorMessage = formatApiError(error)
    console.error('API request failed:', error)

    // 上报API请求错误到Sentry（网络错误或其他异常）
    reportApiError(error, {
      url: endpoint,
      method: config.method,
      message: errorMessage,
    })

    throw new Error(errorMessage)
  }
}
